import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, useEffect } from 'react';
import { Formik, FormikErrors, FormikTouched } from 'formik';
import styles from './UpdateWorkFlow.module.scss';
import { useTranslation } from 'react-i18next';
import { Button, CircleButton } from 'UI';

const { Box, ElementContainer, DetailsCard, Description, Select, MultiSelect, TextField, MaterialSymbol } = vp;
export type UpdateWorkflow = vp.Workflow & {
  TransactionTypes: string[];
};
export type UpdateWorkflowContainer = {
  values: UpdateWorkflow;
  errors?: FormikErrors<UpdateWorkflow>;
  touched?: FormikTouched<UpdateWorkflow>;
  submitForm?: () => Promise<void>;
};
export type UpdateWorkFlowFormProps = {
  id: number;
  defaultValues?: UpdateWorkflow;
  transactionTypesResponse?: vp.TransactionTypesResponse;
  workflows?: vp.Workflow[];
  workflowList: (UpdateWorkflowContainer | undefined)[];
  isDelete: boolean;
  isCreate: boolean;
  isView: boolean;
  isReview: boolean;
  onDelete: (index: number) => void;
  onChange: (
    id: number,
    values: UpdateWorkflow,
    errors: FormikErrors<UpdateWorkflow>,
    touched: FormikTouched<UpdateWorkflow>,
    submitForm: () => Promise<void>,
  ) => void;
};

type FC = FunctionComponent<UpdateWorkFlowFormProps>;

const UpdateWorkFlowForm: FC = ({
  defaultValues = {
    ID: '0',
    VIOAccounts_ID: '0',
    VIOAccountTransactionTypes_ID: '0',
    NumberApprovalsRequired: 1,
    MinAmount: 0,
    IsActive: false,
    DateAdded: '',
    TransactionTypes: [],
  },
  id,
  isCreate,
  transactionTypesResponse,
  workflowList,
  workflows = [],
  isDelete,
  isView,
  isReview = false,
  onDelete,
  onChange,
}: UpdateWorkFlowFormProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'TransactionApproval.Configuration.Workflows.Update.Form',
  });

  return (
    <Formik
      initialValues={{ ...defaultValues }}
      onSubmit={() => {}}
      validationSchema={createdValidationSchema()}
      enableReinitialize
    >
      {({ values, touched, errors, handleChange, handleBlur, submitForm, validateForm }) => {
        useEffect(() => {
          onChange(id, values, errors, touched, submitForm);
        }, [values, errors]);
        useEffect(() => {
          validateForm();
        }, [defaultValues]);
        useEffect(() => {
          if (isCreate) {
            handleChange(vp.createSyntheticEvent('TransactionTypes', []));
          }
        }, []);

        return (
          <DetailsCard
            width="100%"
            content={
              <Box width="100%" position="relative">
                {isDelete && !isReview && (
                  <div className={styles.delete}>
                    <CircleButton
                      colorScheme="destructive"
                      iconFill
                      icon="delete"
                      onClick={() => {
                        onDelete(id);
                      }}
                      size="large"
                      aria-label="Delete workflow"
                    />
                  </div>
                )}
                <Box maxwidth="400px" flx="col">
                  <Box pb="sm" flx="col">
                    <ElementContainer
                      padding="14px 0 12px"
                      label={t('PaymentRails.label')}
                      isBold={false}
                      input={
                        <Box flx="col" width="400px">
                          {isCreate && (
                            <>
                              <MultiSelect
                                name="TransactionTypes"
                                placeholder={t('PaymentRails.placeholder', { defaultValue: 'Select Payment Rails' })}
                                renderSelectedValue={(_, { Placeholder }) => <Placeholder />}
                                disabled={isReview}
                                options={Object.values(transactionTypesResponse?.TransactionTypes || {})
                                  .filter((transactionType) => {
                                    const exists = workflows.some(
                                      (workflow) => workflow.VIOAccountTransactionTypes_ID === transactionType.ID,
                                    );

                                    const existsInOther = workflowList.some(
                                      (workflow, index) =>
                                        index !== id &&
                                        workflow?.values?.TransactionTypes.some(
                                          (transactionTypeWorkflow) =>
                                            transactionTypeWorkflow === transactionType.TransactionType,
                                        ),
                                    );
                                    return !exists && !existsInOther;
                                  })
                                  .map((transactionType) => ({
                                    label: transactionType.TransactionType,
                                    value: transactionType.TransactionType,
                                  }))}
                                value={values.TransactionTypes.filter((t) => t)}
                                onChange={(_, { options }) => {
                                  handleChange(
                                    vp.createSyntheticEvent(
                                      'TransactionTypes',
                                      options.map(({ value }) => value),
                                    ),
                                  );
                                }}
                                errorMsg={
                                  errors.TransactionTypes && touched.TransactionTypes
                                    ? errors.TransactionTypes.toString()
                                    : undefined
                                }
                                renderOption={(o, { Option, SelectedIcon, selected }) => (
                                  <Option>
                                    {o.label}
                                    {selected && <SelectedIcon />}
                                  </Option>
                                )}
                                renderDropdownFooter={() => {
                                  const selectedRails = values.TransactionTypes.filter((t) => t);
                                  const availableOptions = Object.values(
                                    transactionTypesResponse?.TransactionTypes || {},
                                  )
                                    .filter((transactionType) => {
                                      const exists = workflows.some(
                                        (workflow) => workflow.VIOAccountTransactionTypes_ID === transactionType.ID,
                                      );

                                      const existsInOther = workflowList.some(
                                        (workflow, index) =>
                                          index !== id &&
                                          workflow?.values?.TransactionTypes.some(
                                            (transactionTypeWorkflow) =>
                                              transactionTypeWorkflow === transactionType.TransactionType,
                                          ),
                                      );
                                      return !exists && !existsInOther;
                                    })
                                    .map((transactionType) => transactionType.TransactionType);

                                  const allSelected = availableOptions.every((option) =>
                                    selectedRails.includes(option),
                                  );

                                  return allSelected ? (
                                    <Button
                                      onClick={() => {
                                        handleChange(vp.createSyntheticEvent('TransactionTypes', ''));
                                      }}
                                      size="small"
                                    >
                                      {t('PaymentRails.deselectAll', { defaultValue: 'Deselect All' })}
                                    </Button>
                                  ) : (
                                    <Button
                                      onClick={() => {
                                        handleChange(vp.createSyntheticEvent('TransactionTypes', availableOptions));
                                      }}
                                      size="small"
                                    >
                                      {t('PaymentRails.selectAll', { defaultValue: 'Select All' })}
                                    </Button>
                                  );
                                }}
                              />

                              {values.TransactionTypes && values.TransactionTypes.filter((t) => t).length > 0 && (
                                <div className={styles.selectedRails}>
                                  {values.TransactionTypes.filter((t) => t).map((rail) => (
                                    <div key={rail} className={styles.railChip}>
                                      <span className={styles.railChipText}>{rail}</span>
                                      {!isReview && (
                                        <div
                                          className={styles.railChipRemove}
                                          onClick={() => {
                                            const selectedRails = values.TransactionTypes.filter((t) => t);
                                            const newRails = selectedRails.filter((r) => r !== rail);
                                            handleChange(vp.createSyntheticEvent('TransactionTypes', newRails));
                                          }}
                                        >
                                          <MaterialSymbol icon="close" />
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                          {!isCreate && (
                            <TextField
                              width="100%"
                              containerWidth="100%"
                              disabled
                              variant="outlineShadow"
                              value={values.TransactionTypes.join('; ')}
                              name={`appover-rails-${id}`}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={
                                errors.TransactionTypes && touched.TransactionTypes ? errors.TransactionTypes : null
                              }
                            />
                          )}
                        </Box>
                      }
                    />
                  </Box>
                  <Box pb="sm" flx="col">
                    <ElementContainer
                      label={t('NumberOfApprovers.label')}
                      padding="14px 0 12px"
                      isBold={false}
                      input={
                        <Select
                          version="2"
                          options={[
                            { value: '1', label: '1' },
                            { value: '2', label: '2' },
                            { value: '3', label: '3' },
                            { value: '4', label: '4' },
                            { value: '5', label: '5' },
                            { value: '6', label: '6' },
                            { value: '7', label: '7' },
                            { value: '8', label: '8' },
                            { value: '9', label: '9' },
                            { value: '10', label: '10' },
                          ]}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          sort={false}
                          disabled={isView || isReview}
                          errorMsg={
                            errors.NumberApprovalsRequired && touched.NumberApprovalsRequired
                              ? errors.NumberApprovalsRequired
                              : undefined
                          }
                          placeholder={t('NumberOfApprovers.placeholder')}
                          value={
                            values.NumberApprovalsRequired !== null ? values.NumberApprovalsRequired.toString() : ''
                          }
                          name="NumberApprovalsRequired"
                        />
                      }
                    />
                  </Box>
                  <Box pb="sm" flx="col">
                    <ElementContainer
                      label={t('MinimumTransactionAmount.label')}
                      padding="14px 0 0px"
                      isBold={false}
                      input={
                        <Box flx="col" width="100%">
                          <Description size="md" color="#5E5E5E">
                            {t('MinimumTransactionAmount.instruction')}
                          </Description>
                          <TextField
                            name="MinAmount"
                            value={values.MinAmount}
                            preset="currency"
                            variant="outlineShadow"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            disabled={isView || isReview}
                            error={errors.MinAmount && touched.MinAmount ? errors.MinAmount : null}
                          />
                        </Box>
                      }
                    />
                  </Box>
                </Box>
              </Box>
            }
          />
        );
      }}
    </Formik>
  );
};

function createdValidationSchema() {
  return Yup.object().shape({
    MinAmount: Yup.string().required(),
    NumberApprovalsRequired: Yup.string().required(),
    TransactionTypes: Yup.array().of(Yup.string().required()).min(1, 'At least one payment rail must be selected'),
  });
}
export default UpdateWorkFlowForm;
